/**
 * Labstep
 *
 * @module components/Group/Settings/Protocol
 * @desc Group settings for Protocol
 */

import { EntityActionUpdateAllowedActionsLockContent } from 'labstep-web/components/Entity/Action/Update/AllowedActionsLock/Content';
import { EntityActionUpdateCheckbox } from 'labstep-web/components/Entity/Action/Update/Checkbox';
import { GroupFormShowEditCustomShowIdentifierSetProtocolCollection } from 'labstep-web/components/Group/Form/ShowEdit/CustomShowIdentifierSet/ProtocolCollection';
import { GroupFormShowEditDefaultProtocolCollectionFolder } from 'labstep-web/components/Group/Form/ShowEdit/DefaultProtocolCollectionFolder';
import { GroupFormShowEditDefaultProtocolCollectionTemplate } from 'labstep-web/components/Group/Form/ShowEdit/DefaultProtocolCollectionTemplate';
import { LayoutLinks } from 'labstep-web/components/Layout/Links';
import type { LayoutLinksProps } from 'labstep-web/components/Layout/Links/types';
import { OrganizationPremiumFeatureToggle } from 'labstep-web/components/Organization/PremiumFeatureToggle';
import { Flex } from 'labstep-web/core/Flex';
import { HorizontalTable } from 'labstep-web/core/Table/Horizontal';
import type { ITableRowType } from 'labstep-web/core/Table/Horizontal/types';
import { useHasFeatureFlagEnabled } from 'labstep-web/hooks/feature-flag';
import type { Group } from 'labstep-web/models/group.model';
import { TagType } from 'labstep-web/models/tag.model';
import FolderIndex from 'labstep-web/screens/Folder/Index';
import { ProtocolCollectionIndexTemplate } from 'labstep-web/screens/ProtocolCollection/Index/Template';
import { TagIndex } from 'labstep-web/screens/Tag/Index';
import type React from 'react';
import type { GroupSettingsProtocolProps } from './types';

export const getRows = (
  group: Group,
  hasCustomIdentifierFeature: boolean,
) => {
  const rows: ITableRowType<Group>[] = [
    {
      header: 'Allow Unsorted',
      content: () => (
        <Flex column spacing="gap">
          <EntityActionUpdateCheckbox
            entity={group}
            field={
              'is_unsorted_folder_protocol_collection_enabled' as const
            }
            toggle
            optimistic
          />
          If disabled, a collection must always be selected when
          creating a new protocol.
        </Flex>
      ),
    },
    {
      header: 'Default Collection',
      content: () => (
        <Flex column spacing="gap">
          <GroupFormShowEditDefaultProtocolCollectionFolder
            group={group}
          />
          If you set a default collection for this Workspace, all new
          protocols will be stored in this collection by default
          unless another collection is specified at creation. If no
          default folder is set, it will be requested before creating
          a new protocol.
        </Flex>
      ),
    },
    {
      header: 'Default Template',
      content: () => (
        <Flex column spacing="gap">
          <GroupFormShowEditDefaultProtocolCollectionTemplate
            group={group}
          />
          If you set a default template for this Workspace, all new
          protocols will use this template by default unless another
          template is specified at creation.
        </Flex>
      ),
    },
    {
      header: 'Custom Identifier',
      content: () => (
        <Flex column spacing="gap">
          {hasCustomIdentifierFeature ? (
            <GroupFormShowEditCustomShowIdentifierSetProtocolCollection
              group={group}
            />
          ) : (
            <OrganizationPremiumFeatureToggle premiumFeature="custom_identifier" />
          )}
          Automatically adds a prefix to new protocols with a number
          that increments each time i.e SOP001, SOP002, SOP003 etc.
        </Flex>
      ),
    },
  ];

  return rows;
};

export const GroupSettingsProtocol: React.FC<
  GroupSettingsProtocolProps
> = ({ group }) => {
  const hasCustomIdentifierFeature = useHasFeatureFlagEnabled(
    'custom_identifier',
  );
  const links: LayoutLinksProps['links'] = [
    {
      children: 'Protocols',
      route: {
        to: 'group_settings_protocol',
        params: { id: group.id },
      },
      render: () => (
        <HorizontalTable
          padded
          headerProps={{ width: 3 }}
          rows={getRows(group, hasCustomIdentifierFeature)}
          columns={[group]}
        />
      ),
    },
    {
      children: 'Templates',
      route: {
        to: 'group_settings_protocol_templates',
        params: { id: group.id },
      },
      render: () => <ProtocolCollectionIndexTemplate group={group} />,
    },
    {
      children: 'Collections',
      route: {
        to: 'group_settings_protocol_collections',
        params: { id: group.id },
      },
      render: () => <FolderIndex entityType="ProtocolCollection" />,
    },
    {
      children: 'Tags',
      route: {
        to: 'group_settings_protocol_tags',
        params: { id: group.id },
      },
      render: () => (
        <TagIndex group={group} type={TagType.protocol_collection} />
      ),
    },
    {
      children: 'Lock Settings',
      route: {
        to: 'group_settings_protocol_lock_settings',
        params: { id: group.id },
      },
      render: () => (
        <div>
          <EntityActionUpdateAllowedActionsLockContent
            entity={group}
          />
        </div>
      ),
    },
  ];

  return (
    <div id="group-settings-protocol" style={{ flex: 1 }}>
      <LayoutLinks inverted links={links} />
    </div>
  );
};
